import { getParameterByName } from '@amzn/ihm-associate-tools-js-lib'
import { resolveLocale, Locale } from '@amzn/jwo-store-management-platform-components'

export const LOCALE_STORAGE_KEY = 'selectedLocale'

/**
 * Resolves the locale for this session.
 *
 * Precedence:
 *  1. `?locale=` query param — the native iOS/Android shells resolve the device
 *     locale and pass it in, so when we are hosted in a WebView the shell is
 *     authoritative and the webapp must match it. Also lets support and QA pin
 *     a language by URL.
 *  2. The user's stored preference from the in-app language switcher.
 *  3. DEFAULT_LOCALE, via resolveLocale.
 *
 * The store-country default is deliberately NOT part of this chain — it is
 * applied later in provisioning (see ProvisionUtil.determineLocale), because the
 * store's country is not known until store info has been fetched. This function
 * runs at bootstrap, before that.
 */
export const resolveSessionLocale = (): Locale => {
    const fromQueryParam = getParameterByName('locale')
    if (fromQueryParam) {
        return resolveLocale(fromQueryParam)
    }

    return resolveLocale(getStoredLocale())
}

/**
 * Reads the persisted language choice. Returns null when unset or unreadable —
 * localStorage throws in some embedded WebView privacy modes, and a missing
 * preference must degrade to the default rather than break bootstrap.
 */
export const getStoredLocale = (): string | null => {
    try {
        return window.localStorage.getItem(LOCALE_STORAGE_KEY)
    } catch (error) {
        console.warn('JWOStoreManagementPlatform: Could not read stored locale', error)
        return null
    }
}

export const setStoredLocale = (locale: Locale): void => {
    try {
        window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
    } catch (error) {
        console.warn('JWOStoreManagementPlatform: Could not persist locale', error)
    }
}
